/* eslint-disable react/react-in-jsx-scope */
import { isEmpty, extend } from 'underscore';
import { endsWith } from 'underscore.string';
import Promise from 'bluebird';
import React from 'react';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';

import { KeywordEditor } from './editor/keyword-editor-view';
import { parseRule, evaluateKeywordRules } from './parser';
import { keywords } from './constants';

// Replacement text (insert) for when countdown is selected in ad customizer picker
const pickerKeywordInsert = '{KeyWord:}';
const pickerKeywordInsertCursorPosition = pickerKeywordInsert.length - 1;

export class AdCustomizerKeywordAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({ deps: { i18n, instrumentation }, controller }) {
    super({
      name: 'keyword',
      priority: 1000,
      i18n,
      activityName: 'keyword',
      instrumentation,
      controller,
    });
  }

  /* return a reactjs object here */
  getSyntax(analyzeResult) {
    if (!analyzeResult.options) return null;

    const { options: { keywordCase } } = analyzeResult;
    const keywordText = keywords[keywordCase];
    const syntax = `${this.i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label')} {${keywordText}:${this.i18n.getString('KeywordEditor_DefaultText')}}`;
    return (
      <div>
        <span
          className="keywordEditor-syntax"
        >{syntax}
        </span>
        <HelpIcon
          topic="pop_BA_NewAdCreate_KWInsert_Syntax"
          ariaLabel={this.i18n.getString('Click to learn more about {{item}}', { item: this.i18n.getString('AdCustomizer_Syntax_Syntax') })}
        />
      </div>);
  }

  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    return !isEmpty(options);
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback,
  }) {
    const { options } = analyzeResult || {};

    const editor = new KeywordEditor({
      target,
      options,
      i18n: this.i18n,
      updateAndCloseCallback,
    }).render();

    return (<BackboneInstanceWrapper
      managed
      instance={editor}
    />);
  }


  // get menu item
  getMenuItem({
    startIndex, endIndex, updateAndCloseCallback, target,
  }) {
    return {
      name: this.i18n.getString('Picker_Keyword_Label'),
      description: this.i18n.getString('Picker_Keyword_Description'),
      onClick: () => {
        const beginning = target.value.substring(0, startIndex);
        const end = target.value.substring(endIndex);
        updateAndCloseCallback({
          newValue: `${beginning}${pickerKeywordInsert}${end}`,
          cursorPosition: beginning.length + pickerKeywordInsertCursorPosition,
          isSelectedByUser: true,
        });
      },
    };
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target,
  }) {
    const rule = parseRule(text, cursorIndex);

    if (rule) {
      const { options: { startIndex } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: [this.getMenuItem({
            startIndex, endIndex: cursorIndex, updateAndCloseCallback, target,
          })],
        },
        rule
      ));
    }
    // when not match, see whether to show menu item
    if (text && endsWith(text, '{', cursorIndex)) {
      return Promise.resolve({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: [this.getMenuItem({
          startIndex: text.length - 1,
          endIndex: cursorIndex,
          updateAndCloseCallback,
          target,
        })],
      });
    }
    return Promise.resolve(null);
  }

  evaluateRule(text) {
    return evaluateKeywordRules(text);
  }

  getCharCountExcludingAdCustomizers(text) {
    let charCount = 0;
    let processedText = text || '';
    let toContinue = false;

    let tokens = /\{(?:param[123]|keyword)}/i.exec(processedText);
    if (tokens) {
      processedText = processedText.replace(tokens[0], '');
      toContinue = true;
    } else {
      tokens = /\{(?:param[123]|keyword):([^}]*)}/i.exec(processedText);
      if (tokens) {
        processedText = processedText.replace(tokens[0], '');
        charCount += tokens[1] ? tokens[1].length : 0;
        toContinue = true;
      }
    }

    return {
      processedText,
      charCount,
      toContinue,
    };
  }
}
